// Auto-Mix: eure Lieblings-Plugins je Aufgabe (und auf Wunsch je Spurrolle) und deren Reglerzuordnung.
// Gewählt wird aus den Plugins des Studio-Rechners (Bridge). Ist nichts gewählt oder die Bridge nicht da,
// nimmt der Auto-Mix die eingebauten SRS-Plugins. Die Wahl gilt für alle Songs auf diesem Rechner.
import { useEffect, useMemo, useState } from 'react';
import type { PluginParamInfo } from '@srs/shared';
import { useBridge } from './Dante.tsx';
import { describe, pluginLabel, useFx } from '../audio/bridgefx.ts';
import { autoProfile, coverage, loadPrefs, prefFor, profileFor, saveProfile, savePrefs, TASK_KEYS, TASK_LABEL, parseValue, type FxPrefs, type PluginProfile, type Task } from '../automix/pluginmap.ts';
import { ROLE_LABEL, type FxChoice } from '../automix/automix.ts';
import type { Role } from '../automix/measure.ts';

export const FX_TASKS: Task[] = ['eq', 'comp', 'deesser', 'saturation', 'reverb', 'gain'];
const ROLE_TASKS: Task[] = ['eq', 'comp', 'saturation'];
const ROLES: Role[] = ['vox', 'bgv', 'bass', 'kick', 'snare', 'drums', 'guitar', 'keys', 'lead', 'strings', 'other'];

/** Auswahl für applySuggestions aus den gespeicherten Vorlieben (nur Plugins, deren Regler bekannt sind) */
export function fxChoice(prefs: FxPrefs = loadPrefs()): FxChoice {
  const descs = useFx.getState().descs;
  return {
    pick(task, role) {
      const id = prefFor(prefs, task, role);
      const d = id ? descs[id] : undefined;
      if (!d) return null;
      return { pluginId: id, label: pluginLabel(d.vendor, d.name), params: d.params, profile: profileFor(id, task, d.params)! };
    },
  };
}

export function FxPrefsPanel() {
  const bridge = useBridge();
  const descs = useFx((s) => s.descs);
  const [prefs, setPrefsRaw] = useState<FxPrefs>(loadPrefs);
  const [open, setOpen] = useState<Task | null>(null);
  const [mapFor, setMapFor] = useState<{ pluginId: string; task: Task } | null>(null);
  const own = bridge.plugins.filter((p) => !p.isInstrument);
  const setPrefs = (n: FxPrefs) => { setPrefsRaw(n); savePrefs(n); };
  // Regler der gewählten Plugins lesen (für Zuordnung und Auto-Mix)
  useEffect(() => { if (bridge.connected && bridge.paired) for (const id of new Set(Object.values(prefs).filter(Boolean))) void describe(id); }, [prefs, bridge.connected, bridge.paired]);

  const select = (key: string, value: string, fallback?: string) => (
    <select value={value} onChange={(e) => { const n = { ...prefs }; if (e.target.value) n[key] = e.target.value; else delete n[key]; setPrefs(n); }}>
      <option value="">{fallback ?? 'SRS Standard'}</option>
      {own.map((p) => <option key={p.id} value={p.id}>{pluginLabel(p.vendor, p.name)}</option>)}
      {value && !own.some((p) => p.id === value) && <option value={value}>{descs[value]?.name ?? 'nicht verbunden'}</option>}
    </select>
  );

  return (
    <div className="fxp">
      {FX_TASKS.map((task) => {
        const id = prefs[task] ?? '';
        const d = id ? descs[id] : undefined;
        const prof = d ? profileFor(id, task, d.params) : null;
        const cov = prof ? coverage(prof) : null;
        return (
          <div key={task} className="fxp-row">
            <div className="fxp-head">
              <span className="l">{TASK_LABEL[task]}</span>
              {select(task, id)}
            </div>
            {id && (
              <div className="fxp-sub">
                {!d ? <span className="t2">{bridge.connected ? 'Regler werden gelesen …' : 'Bridge nicht verbunden: Auto-Mix nimmt das eingebaute Plugin'}</span>
                  : <>
                    <span className={`pill ${prof?.confirmed ? 'green' : cov && cov.mapped < cov.total ? 'amber' : ''}`}>{prof?.confirmed ? 'Zuordnung bestätigt' : `Zuordnung automatisch: ${cov?.mapped} von ${cov?.total}`}</span>
                    <button className="btn small" onClick={() => setMapFor({ pluginId: id, task })}>Prüfen</button>
                  </>}
              </div>
            )}
            {ROLE_TASKS.includes(task) && (
              <>
                <button className="linkbtn" onClick={() => setOpen(open === task ? null : task)}>{open === task ? 'Je Spurrolle ausblenden' : 'Je Spurrolle festlegen'}{ROLES.some((r) => prefs[`${task}:${r}`]) ? ` (${ROLES.filter((r) => prefs[`${task}:${r}`]).length})` : ''}</button>
                {open === task && (
                  <div className="fxp-roles">
                    {ROLES.map((r) => (
                      <label key={r}><span>{ROLE_LABEL[r]}</span>{select(`${task}:${r}`, prefs[`${task}:${r}`] ?? '', 'wie oben')}</label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      <div className="t2" style={{ fontSize: 12, padding: '4px 2px 0' }}>
        Der Auto-Mix stellt eure Plugins über die Bridge ein; gerechnet wird auf dem Studio-Rechner. Ohne Auswahl nimmt er die SRS-Plugins. Master-Bus bleibt vorerst eingebaut.
      </div>
      {mapFor && descs[mapFor.pluginId] && <MappingDialog pluginId={mapFor.pluginId} task={mapFor.task} params={descs[mapFor.pluginId].params} name={descs[mapFor.pluginId].name} close={() => setMapFor(null)} />}
    </div>
  );
}

/** Bereich eines Reglers aus den Anzeigetexten, z. B. "−60.0 dB … 0.0 dB" */
const rangeOf = (p: PluginParamInfo) => (p.texts.length ? `${p.texts[0]} … ${p.texts[p.texts.length - 1]}` : '');

function MappingDialog({ pluginId, task, params, name, close }: { pluginId: string; task: Task; params: PluginParamInfo[]; name: string; close: () => void }) {
  const [prof, setProf] = useState<PluginProfile>(() => profileFor(pluginId, task, params)!);
  const usable = useMemo(() => params.filter((p) => p.auto), [params]);
  const keys = TASK_KEYS[task];
  return (
    <div className="dlg-back" onPointerDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="dlg" style={{ width: 640, maxWidth: 'calc(100vw - 24px)' }}>
        <div className="pw-head" style={{ cursor: 'default' }}>
          <div className="pw-title"><b>Reglerzuordnung: {name}</b><span>{TASK_LABEL[task]}</span></div>
          <div className="r"><button className="btn small" onClick={close}>✕</button></div>
        </div>
        <div className="pw-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <div className="t2" style={{ fontSize: 12.5, marginBottom: 10 }}>Welcher Regler des Plugins entspricht welchem Wert des Auto-Mix? Der Bereich zeigt, was das Plugin meldet. Bei Reglern ohne Zahlen (z. B. nur Stufen) setzt der Auto-Mix nichts.</div>
          <table className="amtable">
            <thead><tr><th>Auto-Mix</th><th>Regler im Plugin</th><th>Bereich</th></tr></thead>
            <tbody>
              {keys.map((k) => {
                const e = prof.map[k.key];
                const par = e ? params.find((p) => p.i === e.i) : undefined;
                const readable = !par || k.unit === 'rel' || par.texts.some((t) => parseValue(t, k.unit) !== null);
                return (
                  <tr key={k.key} className={!e ? 'low' : ''}>
                    <td style={{ whiteSpace: 'nowrap' }}>{k.label}</td>
                    <td>
                      <select value={e?.i ?? ''} onChange={(ev) => {
                        const map = { ...prof.map };
                        if (ev.target.value === '') delete map[k.key];
                        else map[k.key] = { i: Number(ev.target.value), kind: 'num', ...(k.unit === 'rel' ? { rel: true } : {}) };
                        setProf({ ...prof, map });
                      }}>
                        <option value="">nicht setzen</option>
                        {usable.map((p) => <option key={p.i} value={p.i}>{p.name}</option>)}
                      </select>
                    </td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{par ? rangeOf(par) : '–'}{par && !readable && <div className="warn">keine Zahlen lesbar</div>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <button className="btn primary" onClick={() => { saveProfile({ ...prof, confirmed: true }); close(); }}>Übernehmen</button>
            <button className="btn" onClick={() => setProf(autoProfile(pluginId, task, params))}>Automatisch neu</button>
            <button className="btn" onClick={close}>Abbrechen</button>
          </div>
        </div>
      </div>
    </div>
  );
}
